/**
 * Individual menu item card component
 * Uber Eats-style: clean horizontal layout, large image, clear typography
 */
import { memo, useState } from 'react'
import { Plus, Check } from 'lucide-react'
import { Card, Button } from '@/components/ui'
import { MenuItem } from '@/types/menu'
import { useAppDispatch } from '@/store/hooks'
import { addItem } from '@/store/cartSlice'
import { formatPrice } from '@/utils/formatting'

interface MenuItemCardProps {
  item: MenuItem
  onItemClick: (item: MenuItem) => void
}

export const MenuItemCard = memo(function MenuItemCard({ item, onItemClick }: MenuItemCardProps) {
  const dispatch = useAppDispatch()
  const [justAdded, setJustAdded] = useState(false)

  const canQuickAdd = item.itemType === 'single' && (item.modifierGroups?.length || 0) === 0 && !item.ingredientVariationGroup

  // Calculate price display for items with variations
  const priceDisplay = (() => {
    if (item.ingredientVariationGroup?.variations) {
      const prices = item.ingredientVariationGroup.variations
        .map((v) => v.price)
        .filter((p): p is number => p !== undefined)

      if (prices.length > 0) {
        const minPrice = Math.min(...prices)
        const maxPrice = Math.max(...prices)
        if (minPrice === maxPrice) {
          return formatPrice(minPrice, item.currency)
        }
        return `${formatPrice(minPrice, item.currency)} - ${formatPrice(maxPrice, item.currency)}`
      }
    }
    return formatPrice(item.price, item.currency)
  })()

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (canQuickAdd && item.available) {
      dispatch(
        addItem({
          menuItem: item,
          selectedModifiers: [],
          quantity: 1,
        })
      )
      setJustAdded(true)
      setTimeout(() => setJustAdded(false), 600)
    } else {
      onItemClick(item)
    }
  }

  return (
    <Card
      id={`menu-item-${item.id}`}
      className="group relative overflow-hidden border-0 rounded-2xl transition-all duration-200 cursor-pointer bg-card shadow-card hover:shadow-card-hover hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      role="button"
      tabIndex={0}
      onClick={() => onItemClick(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onItemClick(item)
        }
      }}
    >
      <div className="relative flex items-start justify-between p-3 sm:p-4 gap-4">
        {/* Left side - Text content */}
        <div className="flex-1 min-w-0 py-0.5">
          <h3 className="text-[15px] sm:text-base font-semibold leading-snug mb-1 line-clamp-2">
            {item.name}
          </h3>
          {item.description && (
            <p className="text-[13px] sm:text-sm text-muted-foreground leading-relaxed line-clamp-2 mb-2">
              {item.description}
            </p>
          )}
          <div className="flex items-center gap-2">
            <span className="text-[15px] sm:text-base font-semibold">
              {priceDisplay}
            </span>
            {item.nutritionInfo?.calories && (
              <span className="text-xs text-muted-foreground">
                · {item.nutritionInfo.calories} Cal
              </span>
            )}
          </div>
        </div>

        {/* Right side - Image with add button */}
        <div className="relative flex-shrink-0">
          <div className="w-[108px] h-[108px] sm:w-[128px] sm:h-[128px] rounded-2xl overflow-hidden bg-muted">
            {item.image_url ? (
              <img
                src={item.image_url}
                alt={item.name}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-muted" />
            )}
          </div>

          {/* Add Button */}
          <Button
            variant="white"
            size="icon"
            className={`absolute -bottom-2 -right-2 rounded-full h-11 w-11 shadow-md border-2 border-background transition-all duration-150 ${
              justAdded
                ? 'bg-primary text-primary-foreground scale-100'
                : 'hover:scale-105 active:scale-95'
            }`}
            onClick={handleQuickAdd}
            disabled={!item.available}
            aria-label={canQuickAdd ? 'Quick add' : 'Customize'}
          >
            {justAdded ? (
              <Check className="h-5 w-5" />
            ) : (
              <Plus className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>
    </Card>
  )
})
